import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Search } from "lucide-react";
import { z } from "zod";

import { TextField } from "components/AddVaccineForm/components";
import { CardWithTitle } from "components/helpers/CardWithTitle/CardWithTitle.jsx";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useFindPatientIhi } from "features/patients/hooks/useFindPatientIhi";
import { usePatientHealthIdentifiers } from "features/patients/hooks/usePatientHealthIdentifiers";
import { useSavePatientHealthIdentifiers } from "features/patients/hooks/useSavePatientHealthIdentifiers";
import { formatDate } from "utils/dateFormatter";
import { onInvalidSubmit } from "utils/reportFormErrors";

import s from "./HealthIdentifiers.module.css";

const formSchema = z.object({
  privateInsuranceProvider: z.string().optional(),
  privateInsuranceNumber: z.string().optional(),
  memberNumber: z.string().optional(),
  hwnum: z.string().optional(),
  haaNumber: z
    .string()
    .regex(
      /^R\d{5}[A-Z0-9]?$/,
      "HAA number must be 'R' followed by 5 digits and an optional number or letter"
    )
    .optional()
    .or(z.literal("")),
});

const FIELDS_GENERAL = [
  { name: "privateInsuranceProvider", label: "Health Provider" },
  { name: "privateInsuranceNumber", label: "Private Insurance No." },
  { name: "memberNumber", label: "Member Number" },
  { name: "hwnum", label: "HW Number" },
  { name: "haaNumber", label: "HAA Number" },
];

const FIELDS_IHI = [
  { name: "ihiNumber", label: "IHI" },
  { name: "previousIhiNumber", label: "Previous IHI" },
  {
    name: "ihiLastValidatedDate",
    label: "IHI Last Validated Date",
    type: "date",
  },
];

const HealthIdentifiers = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const patientId = useCurrentPatientId();
  const { data: healthIdentifiers, refetch } =
    usePatientHealthIdentifiers(patientId);
  const saveHealthIdentifiers = useSavePatientHealthIdentifiers();
  const findIhi = useFindPatientIhi();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      privateInsuranceProvider: "",
      privateInsuranceNumber: "",
      memberNumber: "",
      hwnum: "",
      haaNumber: "",
    },
  });

  useEffect(() => {
    if (healthIdentifiers) {
      form.reset({
        privateInsuranceProvider:
          healthIdentifiers.privateInsuranceProvider || "",
        privateInsuranceNumber: healthIdentifiers.privateInsuranceNumber || "",
        memberNumber: healthIdentifiers.memberNumber || "",
        hwnum: healthIdentifiers.hwnum || "",
        haaNumber: healthIdentifiers.haaNumber || "",
      });
    }
  }, [healthIdentifiers]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = (data) => {
    saveHealthIdentifiers.mutate(
      {
        id: healthIdentifiers?.id || null,
        patientId: Number(patientId),
        ...data,
      },
      {
        onSuccess: () => {
          toast.success("Health identifiers saved successfully");
          setIsModalOpen(false);
          refetch();
        },
        onError: (error) => {
          console.error("Failed to save health identifiers:", error);
          toast.error("Failed to save health identifiers. Please try again.");
        },
      }
    );
  };

  const handleFindIhi = () => {
    findIhi.mutate(patientId, {
      onSuccess: (result) => {
        // The endpoint returns HTTP 200 even when the lookup fails at the
        // application level — the real outcome is in the payload. The axios
        // interceptor already unwraps the { status, data } envelope, so the
        // hook resolves to { success, message } directly.
        if (result?.success === true) {
          toast.success("IHI lookup successful");
          refetch();
          return;
        }

        // Explicit backend failure with a reason — surface it verbatim.
        // Anything else (missing payload, missing success flag, missing
        // message) falls back to the generic error.
        const isExplicitFailureWithMessage =
          result?.success === false && !!result?.message;

        toast.error(
          isExplicitFailureWithMessage ? result.message : "IHI lookup failed."
        );
      },
      onError: (error) => {
        console.error("IHI lookup failed:", error);
        toast.error("IHI lookup failed. Please try again.");
      },
    });
  };

  const getDisplayValue = (field) => {
    const empty = "-";
    if (!healthIdentifiers) return empty;
    const value = healthIdentifiers[field.name];
    if (field.name === "ihiLastValidatedDate")
      return formatDate(value) || empty;
    return value || empty;
  };

  const renderFieldList = (fields) =>
    fields.map((field) => (
      <li key={field.name} className={s.listItemContainer}>
        <p className={s.listItemTitle}>{field.label}:</p>
        <p className={s.listItemDescr}>{getDisplayValue(field)}</p>
      </li>
    ));

  const ihiFindButton = (
    <Button
      variant="text"
      onClick={handleFindIhi}
      disabled={findIhi.isPending}
      className={s.editButton}
      endIcon={Search}
    >
      {findIhi.isPending ? "Searching..." : "Find IHI"}
    </Button>
  );

  return (
    <>
      <div className={s.cardsWrapper}>
        <CardWithTitle
          title="Health Identifiers"
          endIcon={Pencil}
          buttonTitle="Edit"
          className={s.editButton}
          onButtonClick={() => setIsModalOpen(true)}
        >
          <ul className={s.listContainer}>{renderFieldList(FIELDS_GENERAL)}</ul>
        </CardWithTitle>

        <CardWithTitle title="HSE IHI" endContent={ihiFindButton}>
          <ul className={s.listContainer}>{renderFieldList(FIELDS_IHI)}</ul>
        </CardWithTitle>
      </div>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Health Identifiers"
        width={900}
      >
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(handleSave, onInvalidSubmit("HealthIdentifiers"))}
            className={s.formContainer}
          >
            <div className={s.formGrid}>
              <TextField
                name="privateInsuranceProvider"
                label="Health Provider"
                placeholder="Enter health provider"
              />
              <TextField
                name="privateInsuranceNumber"
                label="Private Insurance No."
                placeholder="Enter private insurance number"
              />
              <TextField
                name="memberNumber"
                label="Member Number"
                placeholder="Enter member number"
              />
              <TextField
                name="hwnum"
                label="HW Number"
                placeholder="Enter HW number"
              />
              <TextField
                name="haaNumber"
                label="HAA Number"
                placeholder="e.g. R12345"
                onChange={(e) =>
                  form.setValue("haaNumber", e.target.value.toUpperCase(), {
                    shouldValidate: true,
                  })
                }
              />
            </div>
          </form>
        </FormProvider>
        <DialogFooter>
          <Button
            variant="outline"
            size="large"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={form.handleSubmit(handleSave, onInvalidSubmit("HealthIdentifiers"))}
            disabled={saveHealthIdentifiers.isPending}
          >
            {saveHealthIdentifiers.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export { HealthIdentifiers };
